import { autorun, comparer, type IReactionDisposer } from 'mobx'
import debounce from 'lodash/debounce'
import type { StoredPhotoBook } from 'services/PhotoBook/photoBookSaver.types'
import { deepToJS } from 'utils/mobx/deepToJS'
import { readOutsideReaction } from 'utils/mobx/readOutsideReaction'
import { REMOTE_SYNC_DELAY_MS } from '../photoBooksStore/PhotoBooksRemoteSyncer'

const WATCH_DELAY_MS = 150

export type PhotoBookDraftSyncDeps = {
  // Whether the account gate is open and a saver is registered.
  isDraftPushReady: () => boolean
  // Whether every photo this book references has landed in the bank.
  isBookPushable: (book: StoredPhotoBook) => boolean
  // Serializes this book and queues it for the parent's shared batched write.
  // Null when no saver is registered.
  pushBookToDraft: (book: StoredPhotoBook) => Promise<unknown> | null
}

/**
 * Pushes one book into the account's order draft. The draft merges per book id,
 * so this book becomes eligible as soon as its own photos are up, without
 * waiting on the other books, and an unchanged book is never resent.
 */
export class PhotoBookDraftSyncer {
  private readonly bookId: string

  private readonly getBookToSync: () => StoredPhotoBook | null

  private readonly deps: PhotoBookDraftSyncDeps

  private disposers: IReactionDisposer[] = []

  // What the draft holds for this book, as it was last pushed or received.
  private lastPushedBook: StoredPhotoBook | null = null

  constructor(
    bookId: string,
    getBookToSync: () => StoredPhotoBook | null,
    deps: PhotoBookDraftSyncDeps
  ) {
    this.bookId = bookId
    this.getBookToSync = getBookToSync
    this.deps = deps
  }

  // Seeds the skip-unchanged baseline with the copy the account sent, so
  // receiving a book pushes no echo back.
  markBookAlreadyInDraft(book: StoredPhotoBook): void {
    this.lastPushedBook = book
  }

  // A baseline built under one account must not suppress the first push into
  // the next one.
  resetBaseline(): void {
    this.lastPushedBook = null
  }

  // A trailing debounce, not the autorun's own `delay`: MobX's delay is a
  // throttle that re-arms after every run, which would push to the network
  // every few seconds for as long as the user keeps editing.
  private readonly debouncedPush = debounce((book: StoredPhotoBook) => {
    // Both gates are re-checked against the current state: the pushed book is a
    // snapshot, and a photo removed from the bank since it was staged would
    // otherwise serialize as photo: null in the payload.
    if (!readOutsideReaction(() => this.deps.isDraftPushReady())) return
    if (!readOutsideReaction(() => this.deps.isBookPushable(book))) return
    if (comparer.structural(book, this.lastPushedBook)) return

    this.lastPushedBook = book
    this.deps.pushBookToDraft(book)?.catch(() => {
      // The identity check keeps a late failure of this push from wiping a
      // newer one's baseline; the parent's write chain logs the error.
      if (this.lastPushedBook === book) {
        this.lastPushedBook = null
      }
    })
  }, REMOTE_SYNC_DELAY_MS)

  startSyncing(): void {
    this.disposers.push(
      autorun(
        () => {
          const liveBook = this.getBookToSync()
          if (!liveBook) {
            // The book is gone or frozen — a push staged from the previous
            // state must not land after an explicit delete.
            this.debouncedPush.cancel()
            return
          }

          // Snapshotting subscribes this autorun to every leaf of the book, and
          // keeps the observable out of the debounced push.
          const book = deepToJS(liveBook)
          if (!this.deps.isBookPushable(liveBook)) {
            // Photos are still uploading; this re-fires the moment they land.
            this.debouncedPush.cancel()
            return
          }

          // Read here so the account gate opening, or the saver registering
          // after the last edit, stages a push of its own.
          this.deps.isDraftPushReady()

          this.debouncedPush(book)
        },
        {
          name: `PhotoBookDraftSyncer:${this.bookId}`,
          delay: WATCH_DELAY_MS,
        }
      )
    )
  }

  dispose(): void {
    this.debouncedPush.cancel()
    this.disposers.forEach((dispose) => dispose())
    this.disposers = []
  }
}
